import {
    CosmicSourceTypeEnum,
    ICosmicMetric,
    ICosmicMetricReport,
} from "../../../models/CosmicModels";
import { LogComponent, LogElement } from "../../../models/LogModel";
import { LineChartContainer } from "../../azure/common/LineChartContainer";
import React, { useEffect } from "react";
import styles from '../CosmicView.less';
import { useGetCosmicPlatformMetric } from "../../../hooks/useCosmicQuery";
import PlatformMetricsCard from "./PlatformMetricsCard";
import { useDateRange } from "../../../hooks/useDateSelector";
import moment from "moment/moment";
import commonStyles from "../../common/styles/Common.less";
import { useSearchParams } from "react-router-dom";
import { SearchKey } from "../../../models/SearchKey";
import { Button } from "@fluentui/react-components";
import { exportStringToCsv } from "../../../utils/common";
import { ISingleDayMetric } from "../../../models/Metric";
import CosmicSelector from "../CosmicSelector";

interface IPlatformViewProps {
    type: CosmicSourceTypeEnum;
}

interface IMetricsChartsProps {
    cosmicMetricReportSum: ICosmicMetricReport[];
    cosmicMetricReportByRing: ICosmicMetricReport[];
    cosmicMetricReportByRegion: ICosmicMetricReport[];
    isLoadingSum: boolean;
    isLoadingByRing: boolean;
    isLoadingByRegion: boolean;
    metricDataConfig: any[];
    handleDownload: (title: string, metric: ICosmicMetric[]) => void;
}

const MetricsCharts: React.FC<IMetricsChartsProps> = ({
                                                          cosmicMetricReportSum,
                                                          cosmicMetricReportByRing,
                                                          cosmicMetricReportByRegion,
                                                          isLoadingSum,
                                                          isLoadingByRing,
                                                          isLoadingByRegion,
                                                          metricDataConfig,
                                                          handleDownload
                                                      }) => {
    const getOrderedSeries = (data: any[], legends: string[]) => {
        if (legends.length === 0) {
            // For Ring/Region chart, use all available legends
            return data.map(item => ({
                name: item.legend,
                data: item.data.map((d: { date: moment.Moment; metricValue: number }) => [
                    d.date.valueOf(),
                    d.metricValue ?? 0
                ])
            }));
        }

        return legends.map(legend => {
            const item = data.find(d => d.legend === legend);
            return {
                name: legend,
                data: item?.data.map((d: { date: moment.Moment; metricValue: number }) => [
                    d.date.valueOf(),
                    (d.metricValue ?? 0) * (legend.includes('Utilization') ? 100 : 1)
                ]) || []
            };
        });
    };
    const renderChart = (config: any, reportData: any, isLoading: boolean) => (
        <div key={config.title}>
            <div className={styles.chartWithTooltip}>
                <LineChartContainer
                    key={config.title}
                    title={config.title}
                    isLoading={isLoading}
                    isNotCurrency={config.isNotCurrency}
                    series={reportData
                        .filter((singleMetric: ICosmicMetricReport) => singleMetric.metric === config.metric)
                        .flatMap((report: ICosmicMetricReport) => getOrderedSeries(report.data, config.legends)) || []}
                    logProps={{
                        logComponent: config.logComponent,
                        logElement: config.logElement,
                        customProperties: {}
                    }}
                    fillMissingAbnormalData={config.fillMissingAbnormalData}
                    suffix={config.suffix}
                    showArea={config.showArea}
                    ignoreWeekend={config.ignoreWeekend}
                    anomalies={[]}
                />
                {(!isLoading) && (
                    <Button onClick={()=> handleDownload(config.title, config.legends.length === 0? (reportData
                            .filter((singleMetric: ICosmicMetricReport) => singleMetric.metric === config.metric)[0]?.data)
                        : reportData[0]?.data.filter((singleMetric: ICosmicMetric) => config.legends.includes(singleMetric.legend)))} > Download CSV </Button>
                )}
            </div>
        </div>
    );

    return (
        <>
            <div className={`${commonStyles.gridWrapper} ${commonStyles.gridColumn2}`} style={{ padding: "0 0 24px 0" }}>
                {renderChart(metricDataConfig[0], cosmicMetricReportSum, isLoadingSum)}
                {renderChart(metricDataConfig[1], cosmicMetricReportSum, isLoadingSum)}
            </div>
            {renderChart(metricDataConfig[2], cosmicMetricReportByRing, isLoadingByRing)}
            {renderChart(metricDataConfig[3], cosmicMetricReportByRegion, isLoadingByRegion)}
        </>
    );
};

const metricDataConfig = [
    {
        title: "Core Distribution",
        metric: "PlatformCoreView",
        unit: "Cores",
        isNotCurrency: true,
        showArea: true,
        fillMissingAbnormalData: true,
        logComponent: LogComponent.CosmicPlatformView,
        logElement: LogElement.CosmicCoresByCategory,
        ignoreWeekend: true,
        dateFormatStr: "MMM",
        anomalies: [],
        legends: [
            "FragmentationCores",
            "EmptyCores",
            "InfraCores",
            "AppCores",
            "EmptyAppCores"
        ],
        suffix: ''
    },
    {
        title: "CPU Utilization",
        metric: "PlatformCoreView",
        unit: "%",
        isNotCurrency: true,
        showArea: true,
        fillMissingAbnormalData: true,
        logComponent: LogComponent.CosmicPlatformView,
        logElement: LogElement.CosmicUtilizationByCategory,
        ignoreWeekend: true,
        dateFormatStr: "MMM",
        anomalies: [],
        legends: [
            "CpuUtilizationP99"
        ],
        suffix: '%'
    },
    {
        title: "Total Cores Distribution by Ring",
        metric: "TotalCores",
        unit: "Cores",
        isNotCurrency: true,
        showArea: true,
        fillMissingAbnormalData: true,
        logComponent: LogComponent.CosmicPlatformView,
        logElement: LogElement.CosmicCoresByCategory,
        ignoreWeekend: true,
        dateFormatStr: "MMM",
        anomalies: [],
        legends: [],
        suffix: ''
    },
    {
        title: "Total Cores Distribution by Region",
        metric: "TotalCores",
        unit: "Cores",
        isNotCurrency: true,
        showArea: true,
        fillMissingAbnormalData: true,
        logComponent: LogComponent.CosmicPlatformView,
        logElement: LogElement.CosmicCoresByCategory,
        ignoreWeekend: true,
        dateFormatStr: "MMM",
        anomalies: [],
        legends: [],
        suffix: ''
    }
];

const PlatformView: React.FC<IPlatformViewProps> = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { startDate, endDate } = useDateRange();
    const handleDownload = (title: string, metric: ICosmicMetric[]) => {
        if (!metric) {
            return;
        }
        const csvData = prepareCSVData('Value', metric);
        exportStringToCsv(csvData, `${title}_${startDate.format('YYYY-MM')}_${endDate.format('YYYY-MM')}.csv`);
    };

    const prepareCSVData = (metric: string, data: ICosmicMetric[]) => {
        const csvData: [number, string, number][] = [];
        const series = data.map((singleMetric: ICosmicMetric) => ({
            name: singleMetric.legend ?? 'Unknown',
            data: singleMetric.data.map((metric: ISingleDayMetric) => [metric.date.valueOf(), metric.metricValue ?? 0])
        })) || [];

        for (const categoryName in series) {
            const data = series[categoryName].data;
            for (const item of data) {
                const cost = item[1];
                csvData.push([item[0], series[categoryName].name, cost]);
            }
        }

        // Sort by date and cost
        csvData.sort((a, b) => (new Date(a[0]).getTime() - new Date(b[0]).getTime()) || (b[2] - a[2]));

        // Format: Month, Category, Cost
        const rowData = csvData.map((item) => item.join(","));
        return ["Month", "Category", metric].join(",") + "\n" + rowData.join("\n");
    };

    useEffect(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (newSearchParams.get(SearchKey.CosmicSearchFilter)) {
            newSearchParams.delete(SearchKey.CosmicSearchFilter);
        }
        const maxDateOffset = 4;
        const endDate = moment().subtract(maxDateOffset, 'day');
        const startDate = moment(endDate).subtract(30, 'days');
        newSearchParams.set(SearchKey.StartDate, startDate.format("YYYY-MM-DD"));
        newSearchParams.set(SearchKey.EndDate, endDate.format("YYYY-MM-DD"));
        setSearchParams(newSearchParams);
    }, []); // Run once on mount

    const { cosmicMetricReport: cosmicMetricReportSum, isLoading: isLoadingSum } = useGetCosmicPlatformMetric("", false);
    const { cosmicMetricReport: cosmicMetricReportByRing, isLoading: isLoadingByRing } = useGetCosmicPlatformMetric("Ring", false);
    const { cosmicMetricReport: cosmicMetricReportByRegion, isLoading: isLoadingByRegion } = useGetCosmicPlatformMetric("Region", false);

    const comboBoxConfigs= [
        { label: "Workload", filterKey: "Workloads" },
        // { label: "ServiceGroupName", filterKey: "ServiceGroupNames" },
        // { label: "TeamGroupName", filterKey: "TeamGroupNames" },
        // { label: "ServiceName", filterKey: "ServiceNames" },
        { label: "Namespace", filterKey: "Namespaces" },
        { label: "Region", filterKey: "Regions" },
        { label: "Ring", filterKey: "Rings" },
        { label: "NodeOS", filterKey: "NodeOS" },
    ]

    return (
        <div className={styles.container}>
            <CosmicSelector comboBoxConfigs={comboBoxConfigs}/>
            <PlatformMetricsCard cosmicMetricReportSum={cosmicMetricReportSum} isLoadingSum={isLoadingSum} />
            <MetricsCharts
                cosmicMetricReportSum={cosmicMetricReportSum}
                cosmicMetricReportByRing={cosmicMetricReportByRing}
                cosmicMetricReportByRegion={cosmicMetricReportByRegion}
                isLoadingSum={isLoadingSum}
                isLoadingByRing={isLoadingByRing}
                isLoadingByRegion={isLoadingByRegion}
                metricDataConfig={metricDataConfig}
                handleDownload={handleDownload}
            />
        </div>
    );
};
export default PlatformView;